import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    body: 'Recopilamos información personal que usted nos proporciona directamente, como su nombre y dirección de correo electrónico al realizar una compra. No recopilamos información innecesaria ni compartimos sus datos con terceros para fines de marketing.'
  }, {
    heading: 'Uso de la Información',
    body: 'Utilizamos su información personal únicamente para procesar su compra, enviarle el producto digital adquirido y comunicarnos con usted sobre su pedido. No enviamos correos no solicitados ni spam.'
  }, {
    heading: 'Protección de Datos',
    body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    body: 'Nuestro sitio web puede utilizar cookies para mejorar su experiencia de navegación y analizar el tráfico del sitio. Usted puede configurar su navegador para rechazar cookies en cualquier momento.'
  }, {
    heading: 'Sus Derechos',
    body: 'Usted tiene derecho a acceder, corregir o eliminar su información personal en cualquier momento. Para ejercer estos derechos, puede contactarnos a través de los canales indicados en nuestro sitio.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    body: 'El Protocolo Varonil 45+ es un producto digital en formato PDF que se entrega mediante descarga inmediata tras la confirmación del pago. No se realizan envíos físicos.'
  }, {
    heading: 'Uso Personal',
    body: 'El contenido adquirido es para uso personal y exclusivo del comprador. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de Responsabilidad',
    body: 'La información proporcionada en este producto es de carácter educativo e informativo. No sustituye el consejo médico profesional. Consulte a su médico antes de iniciar cualquier programa de ejercicio o cambio en su alimentación.'
  }, {
    heading: 'Resultados',
    body: 'Los resultados pueden variar de persona a persona. Los testimonios y ejemplos mencionados representan resultados individuales y no garantizan que usted obtendrá los mismos resultados.'
  }, {
    heading: 'Política de Reembolso',
    body: 'Debido a la naturaleza digital del producto, todas las ventas son finales una vez que se ha descargado el contenido. Si experimenta problemas técnicos con la descarga, contáctenos para asistencia.'
  }, {
    heading: 'Modificaciones',
    body: 'Nos reservamos el derecho de modificar estos términos y condiciones en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio web.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="py-8 px-6 border-t border-brand-surface bg-brand-dark" data-id="element-42">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-43">
          <p className="font-body text-sm text-brand-muted" data-id="element-44">
            &copy; {new Date().getFullYear()} Protocolo Varonil 45+. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-45">
            <button onClick={() => setActiveModal('privacy')} className="font-body text-sm text-brand-muted hover:text-brand-accent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent rounded" data-id="element-46">
              Privacidad
            </button>
            <span className="text-brand-surface" aria-hidden="true" data-id="element-47">
              |
            </span>
            <button onClick={() => setActiveModal('terms')} className="font-body text-sm text-brand-muted hover:text-brand-accent transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent rounded" data-id="element-48">
              Términos
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-49">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} role="dialog" aria-modal="true" aria-label={content.title} data-id="element-50">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="bg-brand-surface border border-brand-card rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-2xl" data-id="element-51">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-brand-card" data-id="element-52">
                <h3 className="font-heading font-bold text-2xl text-brand-text uppercase" data-id="element-53">
                  {content.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="text-brand-muted hover:text-brand-text transition-colors p-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent" aria-label="Cerrar" data-id="element-54">
                  <X className="w-6 h-6" data-id="element-55" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] space-y-6" data-id="element-56">
                {content.sections.map((section, i) => <div key={i} data-id="element-57">
                    <h4 className="font-heading font-bold text-lg text-brand-accent uppercase mb-2" data-id="element-58">
                      {section.heading}
                    </h4>
                    <p className="font-body text-brand-muted leading-relaxed" data-id="element-59">
                      {section.body}
                    </p>
                  </div>)}

                <p className="font-body text-xs text-brand-muted/60 pt-4 border-t border-brand-card" data-id="element-60">
                  Última actualización:{' '}
                  {new Date().toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}